import hasPermission, { ActivePermission } from '../../Hooks/hasPermission';

/**
 * QUÉ PERMISOS ABREN CADA MÓDULO DEL MENÚ (19-oct-2026).
 *
 * > *"Hay que controlar cuantos modulos vemos en el sidebar por permisos, entre como cliente y veia las opciones de users
 * > deliveries, etc... solo deberia poder ver los modulos a los que tenga acceso"*
 *
 * Hasta aquí el menú mostraba todo a todos, y quien no tenía el permiso abría pantallas que le respondían 403 en cada
 * petición.
 *
 * ## 🔑 Los mismos permisos que pide el backend
 *
 * Cada módulo pide **todos** los de su lista: los de `authorize([...])` del endpoint con el que carga su pantalla. Así el
 * menú no ofrece nada que el servidor vaya a negar, y no esconde nada que el servidor permita. Los usa el menú
 * (`layout/Sidebar.tsx`) y también las rutas de esos módulos (`routes.tsx`), para que escribir la dirección a mano lleve
 * al dashboard en lugar de a una pantalla rota.
 *
 * 🔴 **No es seguridad.** El menú y las rutas viven en el navegador y se pueden saltar. Lo que protege los datos es
 * `authorize()` en el backend, endpoint por endpoint.
 *
 * 📌 El API tiene una copia para quitar de *What's New* el enlace a una pantalla que alguien no puede abrir
 * (`SCREEN_PERMISSIONS` en `src/constants/releaseNotes.ts`). Están en repositorios distintos y ninguna prueba las compara:
 * si cambia el permiso de un módulo, cambian las dos.
 *
 * ⚠️ **Una pantalla que no está aquí no sale en el menú** (falla cerrado). Las pruebas del menú revisan que cada entrada
 * tenga su renglón, así que un módulo nuevo sin decidir quién lo abre rompe la suite en lugar de salirle a todos.
 */
export type ModulePath =
  | '/dashboard'
  | '/global-dashboard'
  | '/global-view'
  | '/loads'
  | '/ocean'
  | '/air'
  | '/new-load'
  | '/tracking'
  | '/pre-alert'
  | '/users'
  | '/clients'
  | '/deliveries'
  | '/catalogs'
  | '/audit';

export const MODULE_PERMISSIONS: Readonly<Record<ModulePath, readonly string[]>> = {
  /** `GET /dashboard` */
  '/dashboard': ['LOADS'],
  /** `GET /shipments/global`, el mismo de Global View. */
  '/global-dashboard': ['SH_GLOBAL'],
  /** `GET /shipments/global`. Las cargas terrestres las trae aparte y aguanta que respondan 403. */
  '/global-view': ['SH_GLOBAL'],
  /** `GET /loads-table` */
  '/loads': ['LOADS'],
  /** `GET /shipments/ocean` */
  '/ocean': ['SH_OCEAN'],
  /** `GET /shipments/air` */
  '/air': ['SH_AIR'],
  /** `POST /generate-new-load`: la pantalla solo sirve para crear. */
  '/new-load': ['LD_CREATE'],
  /** `POST /tracking/lookup` */
  '/tracking': ['TR_LOOKUP'],
  /** `GET /pre-alerts` */
  '/pre-alert': ['PA_VIEW'],
  /** `GET /users` */
  '/users': ['USER'],
  /** `GET /clients` */
  '/clients': ['CLIENTS'],
  /** `GET /deliveries-list`. A un Client_Admin el backend le trae solo las de sus clientes. */
  '/deliveries': ['DELIVERIES'],
  /**
   * El permiso del módulo, el mismo que el backend pide para escribir en los catálogos (con el `CT_*` de la acción, ver
   * `catalogPermissions.ts`). 📌 Leerlos no pide permiso en el backend, pero sin `CATALOGS` no se puede hacer nada ahí.
   */
  '/catalogs': ['CATALOGS'],
  /** `GET /activity`, dentro de Developer. */
  '/audit': ['DEV_ACCESS', 'AU_VIEW'],
};

/**
 * ¿Quien tiene estos permisos puede abrir este módulo del menú?
 *
 * ⚠️ Un módulo que no está en `MODULE_PERMISSIONS` responde `false`: ver la nota de arriba.
 */
export const canOpenModule = (
  permissions: ActivePermission[] | null | undefined,
  path: string
): boolean => {
  const required = (MODULE_PERMISSIONS as Readonly<Record<string, readonly string[] | undefined>>)[path];
  if (!required || required.length === 0) return false;
  return required.every((p) => hasPermission(permissions, p));
};
